import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import { Storage, Code } from '@mui/icons-material';
import type { Repository } from '../../store/api/githubApi';

interface RepositorySelectorProps {
  repositories: Repository[];
  selectedRepoId: number | null;
  onSelectRepo: (repoId: number) => void;
  isLoading?: boolean;
}

export const RepositorySelector: React.FC<RepositorySelectorProps> = ({
  repositories,
  selectedRepoId,
  onSelectRepo,
  isLoading,
}) => {
  const handleChange = (e: SelectChangeEvent<number | ''>) => {
    const val = e.target.value;
    if (typeof val === 'number') {
      onSelectRepo(val);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 3,
        mb: 3.5,
      }}
    >
      {/* Title & Subtitle */}
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: '#0f172a',
            letterSpacing: '-0.025em',
            fontSize: { xs: '1.65rem', sm: '1.9rem' },
            lineHeight: 1.2,
            mb: 0.75,
          }}
        >
          Remediation Planner
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#64748b',
            fontSize: '0.95rem',
            lineHeight: 1.5,
            maxWidth: 580,
          }}
        >
          Select the findings you want to address, preview the estimated health movement, and prepare fixes for review.
        </Typography>
      </Box>

      {/* Repository Selector Dropdown */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'flex-start', md: 'flex-end' },
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#64748b',
            fontSize: '0.72rem',
            mb: 0.75,
          }}
        >
          Repository
        </Typography>

        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 } }}>
          <Select<number | ''>
            value={selectedRepoId ?? ''}
            onChange={handleChange}
            displayEmpty
            disabled={isLoading || repositories.length === 0}
            renderValue={(val) => {
              if (val === '') {
                return (
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Select a repository...
                  </Typography>
                );
              }
              const found = repositories.find((r) => r.id === val);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Storage sx={{ fontSize: 16, color: '#2563eb' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                    {found?.name ?? found?.full_name ?? `Repo #${val}`}
                  </Typography>
                  {found?.language && (
                    <Chip
                      label={found.language}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        bgcolor: '#f1f5f9',
                        color: '#475569',
                      }}
                    />
                  )}
                </Box>
              );
            }}
            sx={{
              bgcolor: '#ffffff',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#0f172a',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover': {
                borderColor: '#cbd5e1',
                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
              },
              '&.Mui-focused': {
                border: '1.5px solid #2563eb',
              },
            }}
          >
            {repositories.map((repo) => {
              const score = repo.latest_debt_score;
              const scoreColor =
                score === null ? '#94a3b8' : score >= 75 ? '#16a34a' : score >= 50 ? '#d97706' : '#dc2626';

              return (
                <MenuItem
                  key={repo.id}
                  value={repo.id}
                  sx={{
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    fontSize: '0.875rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Code sx={{ fontSize: 16, color: '#64748b' }} />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {repo.name}
                    </Typography>
                    {repo.language && (
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        · {repo.language}
                      </Typography>
                    )}
                  </Box>

                  {score !== null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: scoreColor,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontWeight: 700,
                          color: scoreColor,
                        }}
                      >
                        {Math.round(score)}
                      </Typography>
                    </Box>
                  )}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};
